import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import CustomEditor from "../Components/CustomTexteditor";
import GoogleDriveImage from "./GoogleDriveImage";

/* ---------------- Modal ---------------- */
function ConfirmPhotoModal({ show, onChangePhoto, onKeepPhoto }) {
  if (!show) return null;
  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000
    }}>
      <div style={{
        background: "#fff",
        padding: 28,
        borderRadius: 10,
        width: 320,
        textAlign: "center"
      }}>
        <h5 className="mb-3">Change Service Photo?</h5>
        <button className="btn btn-primary me-2" onClick={onChangePhoto}>
          Change Photo
        </button>
        <button className="btn btn-secondary" onClick={onKeepPhoto}>
          Keep Old Photo
        </button>
      </div>
    </div>
  );
}

/* ---------------- Notification ---------------- */
function Notification({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div style={{
      position: "fixed",
      top: 24,
      right: 24,
      background: type === "success" ? "#28a745" : "#dc3545",
      color: "#fff",
      padding: "12px 20px",
      borderRadius: 8,
      zIndex: 2000
    }}>
      {message}
      <button
        onClick={onClose}
        style={{ background: "none", border: "none", color: "#fff", marginLeft: 10 }}
      >
        ×
      </button>
    </div>
  );
}

/* ---------------- MAIN ---------------- */
function ServicesManager() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]);

  const [imageSource, setImageSource] = useState("cloudinary");
  const [imageUrl, setImageUrl] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const [notification, setNotification] = useState({ message: "", type: "success" });
  const headingRef = useRef(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: "", type }), 2500);
  };

  const fetchItems = async () => {
    const snapshot = await getDocs(collection(db, "services"));
    setItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  /* ---------------- ADD / UPDATE ---------------- */
  const handleUpload = async () => {
    if (!title || !description) {
      showNotification("Enter title & description", "error");
      return;
    }

    if (isEditing) {
      setPendingUpdate({
        id: editingId,
        title,
        description,
        imageUrl
      });
      setShowPhotoModal(true);
      return;
    }

    // ADD NEW
    if (imageSource === "url") {
      if (!imageUrl) {
        showNotification("Provide image", "error");
        return;
      }
      await saveService({ title, description, imageUrl });
      return;
    }

    openCloudinary(async (url) => {
      await saveService({ title, description, imageUrl: url });
    });
  };

  const openCloudinary = (callback) => {
    const widget = window.cloudinary.createUploadWidget(
      { cloudName: "dgxhp09em", uploadPreset: "unsigned_preset" },
      (error, result) => {
        if (!error && result?.event === "success") {
          callback(result.info.secure_url);
        }
      }
    );
    widget.open();
  };

  /* ---------------- EDIT MODAL ACTIONS ---------------- */
  const handleChangePhoto = () => {
    setShowPhotoModal(false);

    if (imageSource === "url") {
      saveService(pendingUpdate);
      return;
    }

    openCloudinary(async (url) => {
      await saveService({ ...pendingUpdate, imageUrl: url });
    });
  };

  const handleKeepPhoto = async () => {
    setShowPhotoModal(false);
    await saveService(pendingUpdate);
  };

  /* ---------------- SAVE ---------------- */
  const saveService = async (data) => {
    if (data.id) {
      await updateDoc(doc(db, "services", data.id), {
        title: data.title,
        description: data.description,
        imageUrl: data.imageUrl || "",
        updatedAt: serverTimestamp()
      });
      showNotification("Service updated!");
    } else {
      await addDoc(collection(db, "services"), {
        title: data.title,
        description: data.description,
        imageUrl: data.imageUrl || "",
        createdAt: serverTimestamp()
      });
      showNotification("Service added!");
    }

    resetForm();
    fetchItems();
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setImageUrl("");
    setIsEditing(false);
    setEditingId(null);
    setPendingUpdate(null);
  };

  /* ---------------- EDIT / DELETE ---------------- */
  const handleEdit = (item) => {
    setIsEditing(true);
    setEditingId(item.id);
    setTitle(item.title);
    setDescription(item.description);
    setImageUrl(item.imageUrl || "");
    setImageSource(item.imageUrl ? "url" : "cloudinary");

    setTimeout(() => {
      headingRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "services", id));
    fetchItems();
    showNotification("Service deleted!");
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container mt-4">
      <ConfirmPhotoModal
        show={showPhotoModal}
        onChangePhoto={handleChangePhoto}
        onKeepPhoto={handleKeepPhoto}
      />

      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: notification.type })}
      />

      <h3 ref={headingRef}>Services Manager</h3>

      <input
        className="form-control mb-2"
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <CustomEditor value={description} setValue={setDescription} />

      <select
        className="form-select mt-2"
        value={imageSource}
        onChange={(e) => setImageSource(e.target.value)}
      >
        <option value="cloudinary">Upload via Cloudinary</option>
        <option value="url">Use Google Drive Image</option>
      </select>

      {imageSource === "url" && (
        <GoogleDriveImage onImageReady={(url) => setImageUrl(url)} />
      )}

      <button className="btn btn-success mt-3" onClick={handleUpload}>
        {isEditing ? "Update Service" : "Add Service"}
      </button>

      <div className="row mt-4">
        {items.map((item) => (
          <div key={item.id} className="col-md-4">
            <div className="card mb-3">
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  className="card-img-top"
                  alt=""
                  onError={(e) =>
                    (e.target.src =
                      "https://via.placeholder.com/400x300?text=Image+Unavailable")
                  }
                />
              )}
              <div className="card-body">
                <h5>{item.title}</h5>
                <div dangerouslySetInnerHTML={{ __html: item.description }} />
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ServicesManager;
